// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
// RxJS
import { Observable } from 'rxjs';
// CRUD
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
// Models

// Ngrx
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth/_selectors/auth.selectors';
import { FacilityModel } from '../_models/facility.model';
import { environment } from 'src/environments/environment';
import { cloneDeep, uniqWith, isEqual } from 'lodash-es';

@Injectable()
export class FacilitiesService {
  private authToken;
  private companyId: any;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  private static getFacilitiesListQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = ``;
    if (!filter.isActive && sortField !== 'isActive') fromString = `sort=isActive,desc&`;

    fromString += `page=${pageNumber}&size=${pageSize}&sort=${
      sortField === 'city' ? 'location.city' : sortField
    },${sortOrder}&is_active=${filter.isActive}`;

    if (filter.searchTerm) {
      fromString += `&search_term=${encodeURIComponent(filter.searchTerm)}`;
    } else {
      if (filter.code) fromString += `&code_has=${filter.code}`;
      if (filter.nameEmail)
        fromString += /^[^\s@]*@[^\s@]*$/.test(filter.nameEmail)
          ? `&email_has=${encodeURIComponent(filter.nameEmail)}`
          : `&last_name_has=${encodeURIComponent(filter.nameEmail)}`;
      if (filter.type) fromString += `&facility_type=${encodeURIComponent(filter.type)}`;
      if (filter.city) fromString += `&city_has=${encodeURIComponent(filter.city)}`;
      if (filter.contactsId?.length)
        filter.contactsId.forEach(
          (contactId) => (fromString += `&related_contact_id=${contactId}`),
        );
      if (filter.phone) fromString += `&phone_has=${filter.phone}`;
      if (filter.filledByRobot) fromString += `&filled_by_robot=${filter.filledByRobot}`;
    }

    return fromString;
  }

  // CREATE =>  POST: add a new facility to the server
  createFacility(facility: FacilityModel): Observable<FacilityModel> {
    const url = environment.CORE_BASE + '/companies/' + this.companyId + '/facilities';
    const body = cloneDeep(facility);
    delete body.id;
    delete body.location_id;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    return this.http.post<any>(url, body, { headers });
  }

  // READ
  getAllFacilities(): Observable<FacilityModel[]> {
    const url = environment.CORE_BASE + '/companies/2/facilities?page=0&size=20';
    return this.http.get<FacilityModel[]>(url);
  }

  // Method from server should return QueryResultsModel(items: any[], totalsCount: number)
  // items => filtered/sorted result
  // Server should return filtered/sorted result

  findFacilities(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: FacilitiesService.getFacilitiesListQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(
      `${environment.CORE_BASE}/companies/${this.companyId}/facilities`,
      { params },
    );
  }

  getFacilitiesAsCSV(queryParams: QueryParamsModel): Observable<any> {
    const params = new HttpParams({
      fromString: FacilitiesService.getFacilitiesListQueryString(queryParams),
    });

    return this.http.get(`${environment.CORE_BASE}/companies/${this.companyId}/facilities/csv`, {
      params,
      responseType: 'text',
    });
  }

  // UPDATE => PUT: update the facility on the server
  updateFacility(facility: FacilityModel): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    const url =
      environment.CORE_BASE + '/companies/' + this.companyId + '/facilities/' + facility.id;
    return this.http.put(url, facility, { headers });
  }

  // UPDATE Status
  updateStatusForFacility(id: number, is_active: boolean): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);

    const body = {
      id,
      is_active,
    };
    const url = environment.CORE_BASE + '/facilitystatusupdatesingle';
    return this.http.put(url, body, { headers });
  }

  // UPDATE Status
  updateStatusForFacilities(ids: number[], is_active: boolean): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);

    const body = {
      ids,
      is_active,
    };
    return this.http.put(environment.CORE_BASE, body, { headers });
  }

  // DELETE => delete the facility from the server
  deleteFacility(facility_id: number): Observable<any> {
    const url =
      environment.CORE_BASE + '/companies/' + this.companyId + '/facilities/' + facility_id;
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    return this.http.delete<any>(url, { headers });
  }

  deleteFacilities(facilitiesIds: number[] = []): Observable<any> {
    const url = environment.CORE_BASE + '/multiple-soft-delete-facility';
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);

    const body = {
      ids: facilitiesIds,
    };
    return this.http.post<any>(url, body, { headers });
  }

  searchFacilities(search_term: string, facilityIds: number[] = []): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = `${environment.CORE_BASE}/companies/${this.companyId}/facilities`;
    let fromString = `sort=lastName,asc&search_term=${search_term}`;

    if (facilityIds.length) {
      facilityIds.forEach((id) => (fromString += `&id=${id}`));
    }

    const params = new HttpParams({ fromString });

    return this.http.get<any>(url, { headers: httpHeaders, params });
  }

  searchFacilityType(term: string): Observable<any> {
    return this.http.get<any>(
      `${environment.CORE_BASE}/companies/${this.companyId}/facilities/types?term=${term || ''}`,
    );
  }
  updateFacilityImage(file: File, pid): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    const url = `${environment.CORE_BASE}/companies/${this.companyId}/entities/${pid}/attachment`;
    return this.http.post(url, { avatar: file.name }, { headers });
  }
  uploadFacilityImage(s3, contactFile: File): Observable<any> {
    const url = s3.data.url;
    return this.http.put(url, contactFile, {});
  }
  getFacilitiesByIds(IDs) {
    let ids = '';
    IDs = uniqWith(IDs, isEqual);
    IDs.forEach((element) => (ids = ids + '&id=' + element));
    const params = new HttpParams({ fromString: ids });
    const url =
      environment.CORE_BASE + `/companies/${this.companyId}/facilities?page=0&size=${IDs.length}`;
    return this.http.get<any>(url, { params });
  }
}
